import styled from "@emotion/styled";
import {
    baseUrl,
    HtmlReactParser,
    makeAlphaNumeric,
    translate,
    validEmailRegex,
} from "@kaltura/mediaspace-shared-utils";
import { BaseInput, Button, Link, Tooltip, Typography } from "@kaltura/ds-react-components";
import { FocusEvent, MouseEvent, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { ButtonClickAnalyticsType, KmsTypeLinkAbstract } from "@kaltura/mediaspace-shared-types";
import { ForgotPasswordLink, ForgotPwdLinkInfo } from "./ForgotPasswordLink";
import ReCAPTCHA from "react-google-recaptcha";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

/**
 * additional info for rendering OTP element
 */
interface OTPInfo {
    addCodeLink: boolean;
    autoFocus: boolean;
    tooltipText: string;
}

export interface LoginFormProps {
    /**
     * error text to show above the form fields
     */
    error?: string;

    /**
     * referrer info to be submitted with form data
     */
    referrer?: string;

    /**
     * placeholder text to show in the username field
     */
    usernamePlaceHolder?: string;

    /**
     * placeholder text to show in the password field
     */
    pwdPlaceHolder?: string;

    /**
     * allow autocomplete on form fields
     */
    autocomplete?: "on" | "off";

    /**
     * show "cancel" button (button shouldn't show when guests are not allowed)
     */
    showCancelButton?: boolean;

    /**
     * show forgot-password button (should only show if current adapter supports)
     */
    forgotPwdButtonInfo?: ForgotPwdLinkInfo;

    /**
     * show text-field for OTP
     */
    showOTP?: boolean;

    /**
     * show additional OTP help text
     */
    otpInfo?: OTPInfo;

    /**
     * additional buttons/links to show below form fields
     */
    buttons?: KmsTypeLinkAbstract[];

    /**
     * recaptcha site key
     */
    recaptchaSiteKey?: string | undefined;

    /**
     * recaptcha theme
     */
    recaptchaTheme?: "dark" | "light" | undefined;

    /**
     * hide password field
     */
    hidePassword?: boolean;

    /**
     * form action url - called when form is submitted
     */
    formAction?: string;

    /**
     * the user id format is Email - adds Email format validation
     */
    userIdEmail?: boolean;

    /**
     * recaptcha mode
     */
    recapthcaMode?: "normal" | "invisible" | undefined;

    /**
     * the initial value to populate in the user input field
     */
    userIdInitialValue?: string;

    /**
     * submit button analytics name
     */
    analyticsButtonName: string;

    /**
     * submit button text
     */
    submitButtonText: string;
}

const ErrorText = styled(Typography)(({ theme }) => ({
    width: "100%",
    textAlign: "center",
    color: theme.kaltura.palette.danger.main,
    display: "block",
    margin: theme.spacing(3, 0),
}));

const ValidationErrorText = styled(Typography)(({ theme }) => ({
    color: theme.kaltura.palette.danger.main,
    display: "block",
    margin: theme.spacing(1, 0),
}));

const ControlGroup = styled("div")(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));
const FormLabel = styled(Typography)(({ theme }) => ({
    ...theme.kaltura.typography.formLabel,
    display: "block",
    paddingBottom: theme.spacing(1),
    color: theme.kaltura.palette.tone1,
}));

const SubmitButtonWrap = styled("div")(({ theme }) => ({
    margin: theme.spacing(5, 0, 3, 0),
}));

const MoreButtonsWrap = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    fontSize: theme.typography.pxToRem(theme.kaltura.typography.fontSize),
    flexWrap: "wrap",
    gap: theme.spacing(3),
}));

const CaptchaWrap = styled("div")({
    display: "flex",
    justifyContent: "center",
    // this only affect hidden captcha
    ["& .grecaptcha-badge"]: {
        visibility: "hidden",
    },
});

/**
 * Login form component.
 * the form is submitted as a regular HTML form (and not using js)
 * in order to keep the same flow as the non-js login.
 * data is passed as post.
 * - in the case of successful login, the server redirects to the referrer url.
 * - in case of error, the page is refreshed and rendered again with an error.
 */
export const LoginForm = ({
    error,
    referrer,
    usernamePlaceHolder,
    pwdPlaceHolder,
    autocomplete = "off",
    showCancelButton = false,
    forgotPwdButtonInfo,
    showOTP = false,
    otpInfo,
    buttons,
    recaptchaSiteKey,
    recaptchaTheme = "light",
    hidePassword,
    formAction,
    userIdEmail,
    recapthcaMode,
    userIdInitialValue,
    analyticsButtonName,
    submitButtonText,
}: LoginFormProps) => {
    const [processing, setProcessing] = useState<boolean>(false);
    const { register } = useForm();

    const sendButtonAnalytics = useButtonAnalytics();
    const sendAnalytics = (
        label?: string | number | boolean,
        buttonType: ButtonClickAnalyticsType = ButtonClickAnalyticsType.LOGIN
    ) => {
        if (label) {
            sendButtonAnalytics(`Login - ${label}`, buttonType);
        }
    };
    const onCancelButtonClick = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            event.preventDefault();
            event.stopPropagation();
            if (analyticsButtonName) {
                sendAnalytics(analyticsButtonName + " cancel", ButtonClickAnalyticsType.CLOSE);
            }
            window.history.back();
        },
        [analyticsButtonName, sendAnalytics]
    );

    const config = useContext(ConfigContext);
    const captchaRef = useRef<ReCAPTCHA>(null);

    const [validCaptcha, setValidCaptcha] = useState("");
    const onCaptchaChange = (value: string | null) => {
        setValidCaptcha(value ?? "");
    };

    const [userIdFormatError, setUserIdFormatError] = useState("");
    const validateUsername = useCallback(
        (event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            if (userIdEmail) {
                const email = event.target.value;
                const formatError = !email.match(validEmailRegex) ? translate("Invalid Email format") : "";
                setUserIdFormatError(formatError);
            }
        },
        [userIdEmail]
    );

    useEffect(() => {
        setCaptchaToken();
    }, []);

    const setCaptchaToken = async () => {
        if (recapthcaMode === "invisible") {
            const token = await captchaRef.current?.executeAsync();
            setValidCaptcha(token ?? "");
        }
    };

    const { ref: userNameRef, ...userNameProps } = register("Login[username]");
    const { ref: passwordRef, ...passwordProps } = register("Login[password]");
    const { ref: otpRef, ...otpProps } = register("Login[otp]");
    const dirtyInputs = useRef(false);

    // processing is set to true in form's onSubmit handler, because setting it
    // on the submit button's click handler breaks form submission on chrome
    return (
        <form
            className={"kms-ds-login-form"}
            action={`${baseUrl + (formAction ?? "/user/authenticate")}`}
            method={"POST"}
            onSubmit={() => {
                setProcessing(true);
            }}
        >
            <div role={"alert"}>
                {error && (
                    <div className="kms-ds-form-error kms-ds-login-form-error">
                        <ErrorText variant={"body2"}>{HtmlReactParser(error)}</ErrorText>
                    </div>
                )}
            </div>

            <ControlGroup className="kms-ds-login-form-user-id">
                <label id={"Login-username-label"} htmlFor={"Login-username"}>
                    <FormLabel className={"kms-ds-login-username-label"}>
                        {userIdEmail ? translate("Email") : translate("User ID")}
                    </FormLabel>
                </label>
                <BaseInput
                    inputRef={userNameRef}
                    {...userNameProps}
                    id={"Login-username"}
                    fullWidth={true}
                    autoFocus={true}
                    autoComplete={autocomplete}
                    inputProps={{
                        "aria-label": userIdEmail
                            ? translate("Please fill in email address")
                            : translate("Please fill in user id"),
                    }}
                    required={true}
                    placeholder={usernamePlaceHolder}
                    onBlur={(event) => {
                        if (dirtyInputs.current) {
                            validateUsername(event);
                        }
                    }}
                    onChange={(ev) => {
                        dirtyInputs.current = true;
                        userNameProps.onChange(ev);
                    }}
                    aria-invalid={!!userIdFormatError}
                    defaultValue={userIdInitialValue ?? ""}
                    aria-describedby={"Login-username-error"}
                />
                <div id={"Login-username-error"}>
                    {!!userIdFormatError && (
                        <ValidationErrorText variant={"body2"}>{userIdFormatError}</ValidationErrorText>
                    )}
                </div>
            </ControlGroup>

            {!hidePassword && (
                <ControlGroup className="kms-ds-login-form-password">
                    <label id={"Login-password-label"} htmlFor={"Login-password"}>
                        <FormLabel className={"kms-ds-login-password-label"}>{translate("Password")}</FormLabel>
                    </label>
                    <BaseInput
                        inputRef={passwordRef}
                        {...passwordProps}
                        type={"password"}
                        id={"Login-password"}
                        fullWidth={true}
                        autoComplete={autocomplete}
                        inputProps={{ "aria-label": translate("Please fill in password") }}
                        required={true}
                        placeholder={pwdPlaceHolder}
                    />
                </ControlGroup>
            )}

            {showOTP && (
                <ControlGroup className="kms-ds-login-form-otp">
                    <label id={"Login-otp-label"} htmlFor={"Login-otp"}>
                        <FormLabel className={"kms-ds-login-otp-label"}>
                            {translate("Enter Authentication Code")}
                        </FormLabel>
                    </label>
                    <BaseInput
                        inputRef={otpRef}
                        {...otpProps}
                        id={"Login-otp"}
                        fullWidth={true}
                        autoComplete={"off"}
                        inputProps={{ "aria-label": translate("Please fill in the authenticator code") }}
                        placeholder={translate("Authenticator Code")}
                        autoFocus={otpInfo?.autoFocus}
                    />
                    {otpInfo?.addCodeLink && (
                        <Tooltip placement={"top"} title={otpInfo.tooltipText} describeChild={true}>
                            <Typography variant={"button"} color={"primary"}>
                                {translate("What's authentication code?")}
                            </Typography>
                        </Tooltip>
                    )}
                </ControlGroup>
            )}

            {referrer && (
                <>
                    <input {...register("referrer")} type={"hidden"} defaultValue={referrer} id={"referrer"} />
                    <input {...register("ref")} type={"hidden"} defaultValue={referrer} id={"ref"} />
                </>
            )}

            {recaptchaSiteKey && (
                <CaptchaWrap>
                    <ReCAPTCHA
                        ref={captchaRef}
                        sitekey={recaptchaSiteKey}
                        theme={recaptchaTheme}
                        onChange={onCaptchaChange}
                        hl={config.application.currentLocaleCode}
                        size={recapthcaMode}
                        onExpired={setCaptchaToken}
                    />
                    <input type="hidden" name="captcha" value={validCaptcha} />
                </CaptchaWrap>
            )}

            <SubmitButtonWrap className="kms-ds-login-form-submit-button">
                <Button
                    fullWidth={true}
                    size={"large"}
                    loading={processing}
                    disabled={processing || (!!recaptchaSiteKey && !validCaptcha) || !!userIdFormatError}
                    type={"submit"}
                    data-track={"login"}
                    onClick={() => {
                        sendAnalytics(analyticsButtonName);
                    }}
                >
                    {submitButtonText}
                </Button>
            </SubmitButtonWrap>
            <MoreButtonsWrap className={"kms-ds-login-form-buttons"}>
                {!!forgotPwdButtonInfo && <ForgotPasswordLink {...forgotPwdButtonInfo} />}
                {showCancelButton && (
                    <Link size={"small"} component={"button"} onClick={onCancelButtonClick}>
                        {translate("Cancel")}
                    </Link>
                )}
                {!!buttons
                    && buttons.map((button, index) => {
                        const className =
                            button.attributes && button.attributes["systemName"]
                                ? makeAlphaNumeric(button.attributes["systemName"] as string)
                                : "";
                        let target: string | undefined, rel;
                        if (button.attributes && button.attributes["target"]) {
                            target = button.attributes["target"] as string;
                            rel = "noopener noreferrer";
                        }
                        return (
                            <Link
                                size={"small"}
                                href={button.href}
                                className={className}
                                key={`loginbutton_${index}`}
                                target={target}
                                rel={rel}
                                onClick={() => {
                                    if (button.attributes && button.attributes["data-analytics-value"]) {
                                        sendAnalytics(button.attributes["data-analytics-value"]);
                                    }
                                }}
                            >
                                {button.label}
                            </Link>
                        );
                    })}
            </MoreButtonsWrap>
        </form>
    );
};
